/**
 * UI adapter: bridges React tick scheduler to training use cases (UC-21, UC-22)
 * Enable via architectureFeatures.useTrainingUseCases
 */
import { useCallback } from 'react';
import {
  evaluateScenarioObjectiveUseCase,
  evaluateMilestoneUseCase,
} from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { simulationEngine } from '../../../domain/services/SimulationEngine';
import type {
  EvaluateScenarioObjectiveInput,
  EvaluateScenarioObjectiveOutput,
} from '../../../application/use-cases/EvaluateScenarioObjectiveUseCase';
import type {
  EvaluateMilestoneInput,
  EvaluateMilestoneOutput,
} from '../../../application/use-cases/EvaluateMilestoneUseCase';

/** Run scenario objective evaluation through clean architecture use case (UC-21) */
export function executeScenarioObjectives(
  input: EvaluateScenarioObjectiveInput,
): EvaluateScenarioObjectiveOutput {
  if (!architectureFeatures.useTrainingUseCases) {
    throw new Error('useTrainingUseCases feature flag is disabled');
  }

  return evaluateScenarioObjectiveUseCase.execute(input);
}

/** Run milestone evaluation through clean architecture use case (UC-22) */
export function executeMilestoneCheck(
  input: EvaluateMilestoneInput,
): EvaluateMilestoneOutput {
  if (!architectureFeatures.useTrainingUseCases) {
    throw new Error('useTrainingUseCases feature flag is disabled');
  }

  return evaluateMilestoneUseCase.execute(input);
}

/** Legacy fallback — inline scenario objective checks (migration helper) */
export function executeLegacyScenarioObjectives(
  input: EvaluateScenarioObjectiveInput,
): EvaluateScenarioObjectiveOutput {
  const newlyCompleted: string[] = [];

  const objectives = input.objectives.map((obj) => {
    if (obj.completed) return obj;

    if (
      simulationEngine.checkScenarioObjective(
        obj.description,
        input.stats,
        input.buildingCounts,
        input.research,
        input.violationCount,
      )
    ) {
      newlyCompleted.push(obj.description);
      return { ...obj, completed: true };
    }

    return obj;
  });

  return { objectives, newlyCompleted };
}

/** Legacy fallback — inline milestone checks (migration helper) */
export function executeLegacyMilestoneCheck(
  input: EvaluateMilestoneInput,
): EvaluateMilestoneOutput {
  return evaluateMilestoneUseCase.execute(input);
}

/**
 * Hook helper for App.tsx integration.
 * Returns stable callbacks that run UC-21 / UC-22 when feature flag is on.
 */
export function useTrainingEvaluationRunner() {
  const runScenarioObjectives = useCallback(
    (input: EvaluateScenarioObjectiveInput): EvaluateScenarioObjectiveOutput | null => {
      if (!architectureFeatures.useTrainingUseCases) {
        return null;
      }
      return executeScenarioObjectives(input);
    },
    [],
  );

  const runMilestoneCheck = useCallback(
    (input: EvaluateMilestoneInput): EvaluateMilestoneOutput | null => {
      if (!architectureFeatures.useTrainingUseCases) {
        return null;
      }
      return executeMilestoneCheck(input);
    },
    [],
  );

  return { runScenarioObjectives, runMilestoneCheck };
}